import type { NextLink, Operation } from '@apollo/client/core';
import { Observable } from '@apollo/client/core';
import type { ErrorResponse } from '@apollo/client/link/error';
import { onError } from '@apollo/client/link/error';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import type { OperationDefinitionNode, GraphQLFormattedError } from 'graphql';
import type { NetworkError } from '@apollo/client/errors';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import { logOut } from '@getaccept/lib-shared/src/helpers/log-out.helper';
import type { TokenResponse } from '@getaccept/lib-shared/src/types/token-response';
import { PathName } from '@getaccept/lib-shared/src/enums/PathNames';
import { useAuthStore } from './session/store/auth.store';

const handleSessionExpired = ({
  operation,
  forward,
}: {
  operation: Operation;
  forward: NextLink;
}) => {
  const authStore = useAuthStore();
  return new Observable(observer => {
    axios
      .get('/get/settings/token')
      .then((value: AxiosResponse<TokenResponse>) => {
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            authorization: `Bearer ${value.data.access_token}`,
          },
        }));

        authStore.setToken(value.data.access_token);

        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      })
      .catch(error => {
        observer.error(error);
        logOut();
      });
  });
};

export const getGroupingHash = (message: string) => {
  const groups = [
    'Documents not found: ',
    'Forbidden file: ',
    'Access denied for documents: ',
    'Dsr resource .* not found',
    'Resource .* not found',
    'File not found:',
    'Document communication template already exists on entityId .* with documentId .*',
    'Failed to crate a new revision for documentId: .*. Document have already been approved or signed',
    'Directory .* already exists',
  ];
  return groups.find(group => message.match(group)) || message;
};

const notify = (error: Error, operation: Operation) => {
  bugsnagClient.notify(error, event => {
    event.groupingHash = getGroupingHash(error.message);
    event.addMetadata('GraphQL', {
      error,
      operationName: operation?.operationName,
      variables: operation?.variables,
      type: (operation?.query?.definitions[0] as OperationDefinitionNode)?.operation,
    });
  });
};

const reportToBugsnag = (
  graphQLErrors: readonly GraphQLFormattedError[],
  networkError: NetworkError,
  operation: Operation
) => {
  if (!bugsnagClient) return;
  if (graphQLErrors) {
    graphQLErrors
      .filter(
        error =>
          !error.extensions ||
          error.extensions.code !== 'UNAUTHENTICATED' ||
          error.message !== 'User profile is not active'
      )
      .forEach(graphQLError => {
        if (!graphQLError.message) return;
        const error = new Error(graphQLError.message);
        error.name = (graphQLError.extensions?.code as string) ?? 'GRAPHQL_ERROR';
        notify(error, operation);
      });
  }
  if (networkError && !graphQLErrors?.length) {
    if (networkError?.message === 'Your session expired. Sign in again.') {
      return;
    }
    notify(networkError, operation);
  }
};

export const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
    if (operation.getContext().errorMiddleware && graphQLErrors) {
      graphQLErrors = operation.getContext().errorMiddleware(graphQLErrors);
    }

    if (graphQLErrors) {
      const sessionExpired = graphQLErrors.some(
        ({ extensions }) => extensions?.code === 'UNAUTHENTICATED'
      );
      if (sessionExpired && !operation.getContext().disableTokenRetry) {
        return handleSessionExpired({ operation, forward });
      }
      const inactiveUser = graphQLErrors.some(
        error => error.message === 'User profile is not active'
      );

      if (inactiveUser && !window.location.href.includes(PathName.InactiveUser)) {
        window.location.href = `/${PathName.InactiveUser}`;
      }
    }
    reportToBugsnag(graphQLErrors, networkError, operation);
  }
);
